import { useEffect, useRef } from 'react';
import { LAND_ROW_STEP, LAND_ROWS } from '../content/globeLand';
import { createEarthRenderer } from './earthRenderer';

const IMAGE_BASE = `${process.env.PUBLIC_URL}/images/globe`;
const DEG = Math.PI / 180;
const TILT = 20 * DEG; // north tipped towards the viewer
const START_LNG = 38; // longitude facing the viewer at first (the Middle East)
const SPIN_SPEED = 6 * DEG; // radians per second
const DRAG_SPEED = 0.006; // radians per pixel dragged

// Orbits the channel badges ride, taking badges in turn: size (x radius, in globe radii), how
// flat the ellipse is, tilt in degrees, speed in radians per second, and starting angle.
const ORBITS = [
  { size: 1.58, flat: 0.26, tilt: -8, speed: 0.2, phase: 0.6 },
  { size: 1.42, flat: 0.5, tilt: 26, speed: 0.15, phase: 2.2 },
  { size: 1.5, flat: 0.4, tilt: -32, speed: 0.12, phase: 4.1 },
];

// [lat, lng] of the cities the launch arcs connect.
const CITIES = {
  cairo: [30.04, 31.24],
  riyadh: [24.71, 46.68],
  dubai: [25.2, 55.27],
  istanbul: [41.01, 28.98],
  london: [51.51, -0.13],
  paris: [48.86, 2.35],
  berlin: [52.52, 13.4],
  newYork: [40.71, -74],
  saoPaulo: [-23.55, -46.63],
  johannesburg: [-26.2, 28.05],
  mumbai: [19.08, 72.88],
  singapore: [1.35, 103.82],
  tokyo: [35.68, 139.69],
  sydney: [-33.87, 151.21],
};
const HUBS = new Set(['cairo', 'riyadh', 'dubai']);
const ROUTES = [
  ['dubai', 'london'],
  ['cairo', 'newYork'],
  ['riyadh', 'paris'],
  ['cairo', 'berlin'],
  ['riyadh', 'istanbul'],
  ['cairo', 'saoPaulo'],
  ['cairo', 'johannesburg'],
  ['riyadh', 'mumbai'],
  ['dubai', 'singapore'],
  ['dubai', 'tokyo'],
  ['dubai', 'sydney'],
];

function toVector(lat, lng) {
  const c = Math.cos(lat * DEG);
  return [c * Math.sin(lng * DEG), Math.sin(lat * DEG), c * Math.cos(lng * DEG)];
}

// One point per land dot, spaced evenly along each row (for the dotted globe without WebGL).
function landDots() {
  const dots = [];
  LAND_ROWS.forEach(([lat, ranges]) => {
    const step = LAND_ROW_STEP / Math.max(Math.cos(lat * DEG), 0.1);
    for (let i = 0; i < ranges.length; i += 2) {
      for (let lng = Math.ceil(ranges[i] / step) * step; lng <= ranges[i + 1]; lng += step) {
        dots.push(toVector(lat, lng));
      }
    }
  });
  return dots;
}

// Points along the great circle between two cities, lifted off the surface (more for long hops).
function routePoints(from, to, count = 40) {
  const a = toVector(...CITIES[from]);
  const b = toVector(...CITIES[to]);
  const omega = Math.acos(Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const lift = 0.06 + (0.32 * omega) / Math.PI;
  const points = [];
  for (let i = 0; i <= count; i += 1) {
    const t = i / count;
    const ka = Math.sin((1 - t) * omega) / Math.sin(omega);
    const kb = Math.sin(t * omega) / Math.sin(omega);
    const m = 1 + lift * Math.sin(Math.PI * t);
    points.push([(a[0] * ka + b[0] * kb) * m, (a[1] * ka + b[1] * kb) * m, (a[2] * ka + b[2] * kb) * m]);
  }
  return points;
}

// Stand-in globe for browsers without WebGL: gold land dots on a shaded navy sphere.
function drawDottedGlobe(ctx, dots, { cx, cy, R, rotate }) {
  let g = ctx.createRadialGradient(cx, cy, R * 0.9, cx, cy, R * 1.35);
  g.addColorStop(0, 'rgba(90, 150, 255, 0.3)');
  g.addColorStop(1, 'rgba(90, 150, 255, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 1.35, 0, Math.PI * 2);
  ctx.fill();

  g = ctx.createRadialGradient(cx - R * 0.35, cy - R * 0.4, R * 0.1, cx, cy, R);
  g.addColorStop(0, '#1f4479');
  g.addColorStop(0.6, '#10264a');
  g.addColorStop(1, '#07122a');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();

  const buckets = [[], [], [], [], []];
  dots.forEach((v) => {
    const [x, y, z] = rotate(v);
    if (z > 0.02) buckets[Math.min(4, Math.floor(z * 5))].push(cx + x * R, cy - y * R, z);
  });
  const dotSize = R * 0.0105;
  buckets.forEach((list, b) => {
    ctx.beginPath();
    for (let i = 0; i < list.length; i += 3) {
      const r = dotSize * (0.6 + 0.4 * list[i + 2]);
      ctx.moveTo(list[i] + r, list[i + 1]);
      ctx.arc(list[i], list[i + 1], r, 0, Math.PI * 2);
    }
    ctx.fillStyle = `rgba(255, 214, 140, ${0.22 + b * 0.19})`;
    ctx.fill();
  });
}

// A 3D Earth that turns slowly (and can be dragged), with launch arcs from the region to world
// cities and the club's media channels orbiting it. Layers, back to front: orbit rings behind
// the globe, badges behind it, the Earth (WebGL), then arcs, cities, front rings and badges.
// Badges show their name while in front of the globe and shrink to an icon behind it.
function MediaGlobe({ label, channels }) {
  const wrapRef = useRef(null);
  const backRef = useRef(null);
  const earthRef = useRef(null);
  const frontRef = useRef(null);
  const chipRefs = useRef([]);
  const redrawRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    // Hold on to the canvases: React clears the refs on unmount before this effect's cleanup
    // runs, and the resize observer can still fire in between (the globe was just removed).
    const backCanvas = backRef.current;
    const frontCanvas = frontRef.current;
    const back = backCanvas.getContext('2d');
    const front = frontCanvas.getContext('2d');
    if (!back || !front) return undefined;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let alive = true;
    let earth = createEarthRenderer(earthRef.current, IMAGE_BASE, {
      fade: !reduceMotion,
      onReady: () => alive && draw(),
    });
    let dots = earth ? null : landDots();
    const routes = ROUTES.map(([from, to]) => routePoints(from, to));
    const cities = Object.entries(CITIES).map(([key, [lat, lng]], i) => ({
      v: toVector(lat, lng),
      hub: HUBS.has(key),
      phase: (i * 0.37) % 1,
    }));
    const s = {
      w: 0,
      h: 0,
      dpr: 1,
      chipHalf: 80,
      compact: false,
      spin: -START_LNG * DEG,
      velocity: 0,
      time: 0,
      last: 0,
      dragging: false,
      lastX: 0,
    };

    const resize = () => {
      if (!alive) return;
      s.w = wrap.clientWidth;
      s.h = wrap.clientHeight;
      s.dpr = Math.min(window.devicePixelRatio || 1, 2);
      [backCanvas, frontCanvas].forEach((canvas) => {
        canvas.width = Math.round(s.w * s.dpr);
        canvas.height = Math.round(s.h * s.dpr);
      });
      s.compact = s.w < 520;
      wrap.classList.toggle('is-compact', s.compact);
      // Widest badge with its name showing (the name's full width, plus icon and padding).
      const widths = chipRefs.current
        .filter(Boolean)
        .map((el) => el.querySelector('.media-globe-chip-label').scrollWidth + 52);
      s.chipHalf = s.compact ? 24 : Math.max(60, ...widths) / 2;
      draw();
    };

    function draw() {
      const { w, h } = s;
      if (!alive || !w || !h) return;
      const R = Math.min(w * (s.compact ? 0.3 : 0.34), h * 0.38);
      const cx = w / 2;
      const cy = h / 2;
      const cs = Math.cos(s.spin);
      const sn = Math.sin(s.spin);
      const ct = Math.cos(TILT);
      const st = Math.sin(TILT);
      const rotate = (v) => {
        const x = v[0] * cs + v[2] * sn;
        const z = -v[0] * sn + v[2] * cs;
        return [x, v[1] * ct - z * st, v[1] * st + z * ct];
      };

      // Orbit ellipses in pixels, kept inside the box so the badges never get cut off.
      const orbits = ORBITS.map((o) => {
        const tilt = o.tilt * DEG;
        let rx = Math.max(R * 1.08, Math.min(R * o.size, w / 2 - s.chipHalf - 8));
        let ry = rx * o.flat;
        const reachY = Math.hypot(rx * Math.sin(tilt), ry * Math.cos(tilt));
        const roomY = h / 2 - 24;
        if (reachY > roomY) {
          rx *= roomY / reachY;
          ry *= roomY / reachY;
        }
        return { rx, ry, tilt, cos: Math.cos(tilt), sin: Math.sin(tilt) };
      });
      const ring = (ctx, o, from, to, alpha) => {
        ctx.beginPath();
        ctx.ellipse(cx, cy, o.rx, o.ry, o.tilt, from, to);
        ctx.strokeStyle = `rgba(214, 150, 40, ${alpha})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      };

      back.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
      back.clearRect(0, 0, w, h);
      orbits.forEach((o) => ring(back, o, Math.PI, Math.PI * 2, 0.28));

      if (earth && earth.isLost()) {
        earth = null;
        dots = landDots();
      }
      if (earth) {
        // Phones render the Earth at a slightly lower resolution to go easy on their GPUs.
        const dpr = s.compact ? Math.min(s.dpr, 1.5) : s.dpr;
        earth.render({ width: w, height: h, dpr, cx, cy, radius: R, spin: s.spin, tilt: TILT, now: s.time });
      } else {
        drawDottedGlobe(back, dots, { cx, cy, R, rotate });
      }

      const ctx = front;
      ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      // Launch arcs: a faint path, and a bright pulse travelling along it.
      const visible = (p) => p[2] > 0 || p[0] * p[0] + p[1] * p[1] > 1;
      routes.forEach((points, r) => {
        const projected = points.map(rotate);
        ctx.beginPath();
        let drawing = false;
        projected.forEach((p) => {
          if (!visible(p)) {
            drawing = false;
            return;
          }
          if (drawing) ctx.lineTo(cx + p[0] * R, cy - p[1] * R);
          else ctx.moveTo(cx + p[0] * R, cy - p[1] * R);
          drawing = true;
        });
        ctx.strokeStyle = `rgba(255, 207, 112, ${reduceMotion ? 0.55 : 0.3})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        if (reduceMotion) return;
        const head = ((s.time + r * 0.55) % 3.4) / 1.9;
        if (head > 1.3) return;
        const last = projected.length - 1;
        const end = Math.min(head, 1) * last;
        const start = Math.max(0, head - 0.3) * last;
        ctx.lineWidth = 2;
        for (let j = Math.floor(start); j < end && j < last; j += 1) {
          const a = projected[j];
          const b = projected[j + 1];
          if (visible(a) && visible(b)) {
            ctx.beginPath();
            ctx.moveTo(cx + a[0] * R, cy - a[1] * R);
            ctx.lineTo(cx + b[0] * R, cy - b[1] * R);
            ctx.strokeStyle = `rgba(255, 226, 160, ${((j - start) / Math.max(end - start, 1)) * 0.95})`;
            ctx.stroke();
          }
        }
        const tip = projected[Math.round(end)];
        if (head <= 1 && visible(tip)) {
          const px = cx + tip[0] * R;
          const py = cy - tip[1] * R;
          const g = ctx.createRadialGradient(px, py, 0, px, py, 7);
          g.addColorStop(0, 'rgba(255, 240, 200, 0.95)');
          g.addColorStop(1, 'rgba(255, 207, 112, 0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(px, py, 7, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Cities, with a ring pulsing out of each.
      cities.forEach((city) => {
        const p = rotate(city.v);
        if (p[2] <= 0.05) return;
        const px = cx + p[0] * R;
        const py = cy - p[1] * R;
        const pulse = reduceMotion ? 0.4 : (s.time * 0.55 + city.phase) % 1;
        ctx.beginPath();
        ctx.arc(px, py, (city.hub ? 3 : 2) + pulse * (city.hub ? 10 : 7), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 207, 112, ${(1 - pulse) * 0.7 * p[2]})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(px, py, city.hub ? 3 : 2.2, 0, Math.PI * 2);
        ctx.fillStyle = city.hub ? '#ffcf70' : 'rgba(255, 236, 200, 0.95)';
        ctx.fill();
      });

      // Front halves of the orbits.
      orbits.forEach((o) => ring(ctx, o, 0, Math.PI, 0.55));

      // Channel badges ride the orbits: bigger, named and on top at the front; dimmer, icon-only
      // and behind the globe at the back. Narrow screens name only the badge closest to the front.
      const chips = chipRefs.current.filter(Boolean);
      let frontIndex = -1;
      let frontDepth = 0.5;
      chips.forEach((el, i) => {
        const o = orbits[i % orbits.length];
        const cfg = ORBITS[i % ORBITS.length];
        const onOrbit = Math.ceil((chips.length - (i % orbits.length)) / orbits.length);
        const angle = cfg.phase + s.time * cfg.speed + (Math.floor(i / orbits.length) * Math.PI * 2) / onOrbit;
        const depth = Math.sin(angle);
        const ex = o.rx * Math.cos(angle);
        const ey = o.ry * depth;
        const px = cx + ex * o.cos - ey * o.sin;
        const py = cy + ex * o.sin + ey * o.cos;
        const near = (depth + 1) / 2;
        el.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%) scale(${0.78 + 0.22 * near})`;
        el.style.opacity = String(0.5 + 0.5 * near);
        el.style.zIndex = depth > 0 ? '5' : '2';
        el.classList.toggle('is-lit', depth > 0.08);
        if (depth > frontDepth) {
          frontDepth = depth;
          frontIndex = i;
        }
      });
      chips.forEach((el, i) => el.classList.toggle('is-front', i === frontIndex));
    }
    redrawRef.current = resize;

    let raf = 0;
    const loop = (now) => {
      const dt = s.last ? Math.min((now - s.last) / 1000, 0.05) : 0;
      s.last = now;
      s.time += dt;
      if (!s.dragging) {
        s.spin += (SPIN_SPEED + s.velocity) * dt;
        s.velocity *= Math.exp(-2.5 * dt);
      }
      draw();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (raf || reduceMotion) return;
      s.last = 0;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    // Dragging sideways turns the globe; a flick keeps it spinning for a moment.
    const onDown = (event) => {
      s.dragging = true;
      s.lastX = event.clientX;
      s.velocity = 0;
      wrap.setPointerCapture?.(event.pointerId);
    };
    const onMove = (event) => {
      if (!s.dragging) return;
      const delta = (event.clientX - s.lastX) * DRAG_SPEED;
      s.lastX = event.clientX;
      s.spin += delta;
      s.velocity = delta * 60;
      if (reduceMotion) draw();
    };
    const onUp = () => {
      s.dragging = false;
      if (reduceMotion) s.velocity = 0;
    };
    wrap.addEventListener('pointerdown', onDown);
    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerup', onUp);
    wrap.addEventListener('pointercancel', onUp);

    // Only animate while the globe is on screen.
    const io = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()));
    io.observe(wrap);
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();

    return () => {
      alive = false;
      stop();
      io.disconnect();
      ro.disconnect();
      wrap.removeEventListener('pointerdown', onDown);
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerup', onUp);
      wrap.removeEventListener('pointercancel', onUp);
      earth?.dispose();
      redrawRef.current = null;
    };
  }, []);

  // Badges change width with the language: measure and place them again.
  useEffect(() => {
    redrawRef.current?.();
  }, [channels]);

  return (
    <div ref={wrapRef} className="media-globe" role="img" aria-label={label}>
      <canvas ref={backRef} className="media-globe-layer media-globe-back" aria-hidden="true" />
      <canvas ref={earthRef} className="media-globe-layer media-globe-earth" aria-hidden="true" />
      <canvas ref={frontRef} className="media-globe-layer media-globe-front" aria-hidden="true" />
      {channels.map((channel, i) => (
        <span
          key={channel.label}
          ref={(el) => {
            chipRefs.current[i] = el;
          }}
          className="media-globe-chip"
          aria-hidden="true"
        >
          <span className="material-symbols-outlined">{channel.icon}</span>
          <span className="media-globe-chip-label">{channel.label}</span>
        </span>
      ))}
    </div>
  );
}

export default MediaGlobe;
